import { Seo } from '../components/academy/Seo';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Upload, X, ArrowRight, Loader2, ArrowLeft, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 10;
const field = 'w-full bg-zinc-900/70 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500/70 focus:ring-2 focus:ring-orange-500/20 transition';

const FirstLookForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState({
    firstName: '', lastName: '', phone: '', email: '', listingUrl: '', vin: '',
    year: '', make: '', model: '', askingPrice: '', mileage: '',
    sellerType: 'private', preferredContact: 'email', description: '', concern: '', consent: false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFiles = (e) => {
    const incoming = Array.from(e.target.files || []);
    const combined = [...photos, ...incoming].slice(0, MAX_PHOTOS);
    if (photos.length + incoming.length > MAX_PHOTOS) toast.info(`Up to ${MAX_PHOTOS} files.`);
    setPhotos(combined);
  };
  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error('Please agree to be contacted by CarLens.'); return; }
    for (const [k, label] of [['firstName', 'First name'], ['lastName', 'Last name'], ['phone', 'Phone'], ['email', 'Email'], ['year', 'Year'], ['make', 'Make'], ['model', 'Model']]) {
      if (!String(form[k]).trim()) { toast.error(`${label} is required.`); return; }
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, k === 'consent' ? (v ? 'true' : 'false') : v));
      photos.forEach((p) => fd.append('photos', p));
      const res = await axios.post(`${API}/firstlook`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const co = await axios.post(`${API}/firstlook/${res.data.id}/checkout`, { product: 'consultation', origin_url: window.location.origin });
      window.location.href = co.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0e0e10 0%, #17171a 55%, #0e0e10 100%)' }}>
      <Seo siteName="CarLens" title="Book Your CarLens FirstLook™ — 1-on-1 Car Buying Consultation" description="Book your CarLens FirstLook™ — a private 30-minute virtual car buying consultation with a CarLens expert. Introductory offer $49. Available nationwide." path="/firstlook/start" />
      <div className="max-w-3xl mx-auto px-5 py-12 md:py-16">
        <button onClick={() => navigate('/firstlook')} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 text-sm" data-testid="firstlook-back">
          <ArrowLeft className="w-4 h-4" /> Back to FirstLook
        </button>

        <div className="inline-flex items-center gap-2 rounded-full border border-orange-500/40 bg-orange-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-orange-300 mb-5">
          <Video className="w-4 h-4" /> CarLens FirstLook™
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight leading-[1.05]" style={{ fontFamily: 'Space Grotesk' }} data-testid="firstlook-form-h1">
          Book Your <span className="text-orange-500">FirstLook</span> Consultation.
        </h1>
        <p className="mt-4 text-lg text-zinc-300 leading-relaxed">
          Tell us about the vehicle you're considering. After checkout you'll choose a time for your private 30-minute video consultation, and we'll review everything together.
        </p>
        <div className="mt-4 inline-flex items-baseline gap-2 rounded-xl border border-orange-500/40 bg-black/40 px-5 py-2.5">
          <span className="text-zinc-500 line-through text-lg">$79</span>
          <span className="text-3xl font-extrabold text-orange-500" style={{ fontFamily: 'Space Grotesk' }}>$49</span>
          <span className="text-xs uppercase tracking-widest text-orange-300 font-semibold ml-1">Intro Offer</span>
        </div>

        <form onSubmit={submit} className="mt-10 space-y-8" data-testid="firstlook-form">
          <section className="space-y-4">
            <h2 className="text-sm font-bold uppercase tracking-wide text-orange-400">Your Info</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-zinc-300">First name *</Label><Input className={`${field} mt-1.5`} value={form.firstName} onChange={(e) => set('firstName', e.target.value)} data-testid="fl-firstName" /></div>
              <div><Label className="text-zinc-300">Last name *</Label><Input className={`${field} mt-1.5`} value={form.lastName} onChange={(e) => set('lastName', e.target.value)} data-testid="fl-lastName" /></div>
              <div><Label className="text-zinc-300">Mobile phone *</Label><Input className={`${field} mt-1.5`} value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="fl-phone" /></div>
              <div><Label className="text-zinc-300">Email *</Label><Input type="email" className={`${field} mt-1.5`} value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="fl-email" /></div>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-sm font-bold uppercase tracking-wide text-orange-400">The Vehicle</h2>
            <div><Label className="text-zinc-300">Listing URL</Label><Input className={`${field} mt-1.5`} placeholder="https://..." value={form.listingUrl} onChange={(e) => set('listingUrl', e.target.value)} data-testid="fl-listingUrl" /></div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div><Label className="text-zinc-300">Year *</Label><Input className={`${field} mt-1.5`} value={form.year} onChange={(e) => set('year', e.target.value)} data-testid="fl-year" /></div>
              <div><Label className="text-zinc-300">Make *</Label><Input className={`${field} mt-1.5`} value={form.make} onChange={(e) => set('make', e.target.value)} data-testid="fl-make" /></div>
              <div><Label className="text-zinc-300">Model *</Label><Input className={`${field} mt-1.5`} value={form.model} onChange={(e) => set('model', e.target.value)} data-testid="fl-model" /></div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div><Label className="text-zinc-300">Asking price</Label><Input className={`${field} mt-1.5`} placeholder="$" value={form.askingPrice} onChange={(e) => set('askingPrice', e.target.value)} data-testid="fl-askingPrice" /></div>
              <div><Label className="text-zinc-300">Mileage</Label><Input className={`${field} mt-1.5`} value={form.mileage} onChange={(e) => set('mileage', e.target.value)} data-testid="fl-mileage" /></div>
              <div>
                <Label className="text-zinc-300">Seller type</Label>
                <select className={`${field} mt-1.5`} value={form.sellerType} onChange={(e) => set('sellerType', e.target.value)} data-testid="fl-sellerType">
                  <option value="private">Private seller</option>
                  <option value="dealer">Dealer</option>
                </select>
              </div>
            </div>
            <div><Label className="text-zinc-300">VIN (if available)</Label><Input className={`${field} mt-1.5`} value={form.vin} onChange={(e) => set('vin', e.target.value)} data-testid="fl-vin" /></div>
            <div><Label className="text-zinc-300">Your biggest question or concern</Label><textarea rows={3} className={`${field} mt-1.5 resize-none`} placeholder="What worries you most about this vehicle? What do you want us to focus on?" value={form.concern} onChange={(e) => set('concern', e.target.value)} data-testid="fl-concern" /></div>
          </section>

          <section>
            <h2 className="text-sm font-bold uppercase tracking-wide text-orange-400 mb-1">Photos & Vehicle History (optional)</h2>
            <p className="text-xs text-zinc-500 mb-3">Add listing photos, screenshots, or a Carfax/AutoCheck report. You can also add more after booking.</p>
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-zinc-700 rounded-xl py-8 cursor-pointer hover:border-orange-500/60 transition" data-testid="fl-photo-drop">
              <Upload className="w-6 h-6 text-zinc-400" />
              <span className="text-sm text-zinc-400">Tap to add photos or history report</span>
              <input type="file" accept="image/*,.pdf" multiple className="hidden" onChange={onFiles} data-testid="fl-photos" />
            </label>
            {photos.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-4">
                {photos.map((p, i) => (
                  <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-zinc-700 flex items-center justify-center bg-zinc-900">
                    {p.type?.startsWith('image/') ? <img src={URL.createObjectURL(p)} alt="upload" className="w-full h-full object-cover" /> : <span className="text-[10px] text-zinc-400 px-2 text-center break-all">{p.name}</span>}
                    <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-black/70 rounded-full p-1 opacity-0 group-hover:opacity-100 transition" data-testid={`fl-remove-photo-${i}`}><X className="w-3.5 h-3.5 text-white" /></button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <label className="flex items-start gap-3 cursor-pointer bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
            <input type="checkbox" checked={form.consent} onChange={(e) => set('consent', e.target.checked)} className="mt-1 w-4 h-4 accent-orange-500" data-testid="fl-consent" />
            <span className="text-sm text-zinc-300">I agree to be contacted by CarLens by phone, text, or email about my consultation and CarLens services.</span>
          </label>

          <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-5 h-auto min-h-[3.25rem] text-lg font-semibold rounded-lg" data-testid="fl-submit">
            {loading ? <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Redirecting to checkout…</span> : <span className="flex items-center gap-2">Continue to Payment — $49 <ArrowRight className="w-5 h-5" /></span>}
          </Button>
          <p className="text-center text-xs text-zinc-500">Secure checkout. After payment you'll pick your consultation time.</p>
        </form>
      </div>
    </div>
  );
};

export default FirstLookForm;
